import React, { useState } from 'react';
import { FileText, Download, Calendar } from 'lucide-react';
import apiClient from '../api/client';

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateRange, setDateRange] = useState('30');

  const reportTypes = [
    {
      id: 'kpi-trends',
      name: 'KPI Trend Report',
      description: 'Comprehensive analysis of KPI performance over time',
      icon: FileText,
      color: 'blue'
    },
    {
      id: 'capa-aging',
      name: 'CAPA Aging Report',
      description: 'Detailed breakdown of CAPA aging and closure times',
      icon: FileText,
      color: 'yellow'
    },
    {
      id: 'iso-compliance',
      name: 'ISO Compliance Report',
      description: 'ISO 9001:2015 compliance status by department and clause',
      icon: FileText,
      color: 'green'
    },
    {
      id: 'quality-health',
      name: 'Quality Health Summary',
      description: 'Weekly overview of quality performance and risks',
      icon: FileText,
      color: 'purple'
    },
    {
      id: 'leadership',
      name: 'Leadership Accountability Report',
      description: 'Executive summary of performance, missed KPIs, and overdue actions',
      icon: FileText,
      color: 'red'
    },
    {
      id: 'training-compliance',
      name: 'Training Compliance Report',
      description: 'Training completion rates and pending acknowledgments',
      icon: FileText,
      color: 'indigo'
    }
  ];

  const handleGenerateReport = async (reportId, format = 'pdf') => {
    try {
      let endpoint = '';
      let filename = '';

      switch (reportId) {
        case 'kpi-trends':
          endpoint = '/reports/kpi-trends/pdf';
          filename = `kpi_trends_${new Date().toISOString().split('T')[0]}.pdf`;
          break;
        case 'capa-aging':
          endpoint = '/reports/capa-aging/pdf';
          filename = `capa_aging_${new Date().toISOString().split('T')[0]}.pdf`;
          break;
        case 'executive-summary':
        case 'leadership':
          endpoint = '/reports/executive-summary/pdf';
          filename = `executive_summary_${new Date().toISOString().split('T')[0]}.pdf`;
          break;
        case 'iso-compliance':
          alert('ISO Compliance report PDF generation coming soon!');
          return;
        case 'quality-health':
          alert('Quality Health report PDF generation coming soon!');
          return;
        case 'training-compliance':
          alert('Training Compliance report PDF generation coming soon!');
          return;
        default:
          alert('Report type not yet implemented');
          return;
      }

      if (format === 'csv' && reportId === 'kpi-trends') {
        endpoint = `/export/kpi-values/csv?days=${dateRange}`;
        filename = `kpi_values_${new Date().toISOString().split('T')[0]}.csv`;
      } else if (format === 'csv' && reportId === 'capa-aging') {
        endpoint = '/export/capas/csv';
        filename = `capas_${new Date().toISOString().split('T')[0]}.csv`;
      }

      const response = await apiClient.get(endpoint, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      alert(`Report downloaded successfully: ${filename}`);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Reports Center</h1>
        <p className="mt-2 text-gray-600">Generate and download quality management reports</p>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4">
          <Calendar className="text-gray-600" size={24} />
          <span className="text-sm font-medium text-gray-700">Report Period:</span>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="date-range-selector"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>
      </div>

      {/* Report Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          const colorClasses = {
            blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100',
            yellow: 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100',
            green: 'bg-green-50 text-green-600 hover:bg-green-100',
            purple: 'bg-purple-50 text-purple-600 hover:bg-purple-100',
            red: 'bg-red-50 text-red-600 hover:bg-red-100',
            indigo: 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
          };

          return (
            <div
              key={report.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedReport(report)}
              data-testid={`report-card-${report.id}`}
            >
              <div className="p-6">
                <div className={`inline-flex p-3 rounded-lg ${colorClasses[report.color]}`}>
                  <Icon size={24} />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">{report.name}</h3>
                <p className="mt-2 text-sm text-gray-600">{report.description}</p>
                
                <div className="mt-6 flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGenerateReport(report.id, 'pdf');
                    }}
                    className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    data-testid={`generate-${report.id}-pdf`}
                  >
                    <Download size={16} className="mr-2" />
                    PDF
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGenerateReport(report.id, 'csv');
                    }}
                    className="flex-1 flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    data-testid={`generate-${report.id}-csv`}
                  >
                    <Download size={16} className="mr-2" />
                    CSV
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Report Details Modal (if selected) */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6">
            <h2 className="text-2xl font-bold text-gray-900">{selectedReport.name}</h2>
            <p className="mt-2 text-gray-600">{selectedReport.description}</p>
            
            <div className="mt-6 space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Report Contents:</h3>
                <ul className="mt-2 space-y-2 text-gray-600">
                  <li>• Executive summary</li>
                  <li>• Detailed metrics and trends</li>
                  <li>• Visual charts and graphs</li>
                  <li>• Action items and recommendations</li>
                  <li>• ISO compliance documentation</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Export Options:</h3>
                <p className="mt-2 text-gray-600">
                  Reports can be exported in PDF format (for presentations and audits) or CSV format (for data analysis).
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setSelectedReport(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleGenerateReport(selectedReport.id, 'pdf');
                  setSelectedReport(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Generate Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;